import api from './api';

// Deploy note for this repo: the pipeline installs with `npm ci`, which fails outright
// if package-lock.json disagrees with package.json. Running `npm install` locally on a
// newer npm rewrites the lock (it prunes optional peer entries) and breaks the build for
// everyone — so install with `npm ci` here, never `npm install`.

export interface LeadPayload {
  first_name: string;
  last_name: string;
  id_number: string;
  obligation_number: string;
  phone: string;
}

export interface LeadResponse {
  id: number;
  sub_clinic: string;
  id_number: string;
  status: string;
}

export interface LeadCreateResult {
  lead: LeadResponse;
  warnings: string[];
}

export async function createLead(payload: LeadPayload): Promise<LeadCreateResult> {
  const res = await api.post('/leads/', payload);
  return res.data;
}

export async function uploadReferralFile(leadId: number, file: File): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  await api.post(`/leads/${leadId}/referral-file`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** One row of the coordinator's own list. The district is never sent: the
 *  server takes it from her user, so there is no district to pass and none to
 *  get wrong. */
export interface Lead {
  id: number;
  sub_clinic: string;
  first_name: string;
  last_name: string;
  id_number: string;
  obligation_number: string;
  phone: string;
  status: 'CREATED' | 'MATCHED' | 'NOT_INTERESTED';
  matched_at: string | null;
  questionnaire_sent_at: string | null;
  referral_file_id: number | null;
  /** What the IL system last reported about the patient behind this referral.
   *  Stored verbatim there, so an unrecognised value has to show itself rather
   *  than fall into a bucket - see leadStage. */
  patient_id?: number | null;
  patient_status?: string | null;
  patient_status_at?: string | null;
  patient_ever_seen?: boolean | null;
  created_at: string;
}

export interface LeadListResult {
  items: Lead[];
  total: number;
}

/** Which column `search` looks in. Mirrors LEAD_SEARCH_FIELDS on the server;
 *  'all' is the default and what this screen sent before the picker existed. */
export type LeadSearchField =
  | 'all' | 'name' | 'id_number' | 'phone' | 'obligation_number';

export async function listMyLeads(params: {
  search?: string;
  searchField?: LeadSearchField;
  /** Several at once: "waiting" and "declined" are one question when she is
   *  deciding who to chase. Sent comma separated. */
  statuses?: string[];
  /** Not a status - a lead nobody answered and a lead nobody was ever sent both
   *  sit in CREATED, and this is the only thing that separates them. */
  questionnaireSent?: boolean;
  skip?: number;
  limit?: number;
} = {}): Promise<LeadListResult> {
  const query = new URLSearchParams({
    skip: String(params.skip ?? 0),
    limit: String(params.limit ?? 15),   // same default as the server's
    sort_by: 'created_at',
    sort_order: 'desc',
  });
  if (params.search) query.set('search', params.search);
  // Only when it narrows: 'all' is the server's own default, so leaving it out
  // keeps the request identical to what it was.
  if (params.search && params.searchField && params.searchField !== 'all') {
    query.set('search_field', params.searchField);
  }
  if (params.statuses?.length) query.set('status', params.statuses.join(','));
  if (params.questionnaireSent !== undefined) {
    query.set('questionnaire_sent', String(params.questionnaireSent));
  }
  const res = await api.get(`/leads/?${query}`);
  return res.data;
}
